import React, { useState, useEffect } from 'react';
import { LayoutDashboard, Plus, User, LogOut, Menu, X, Store, Package, Award, ChevronLeft, ChevronRight, Pencil, Trash2, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AddStore from '../pages/AddStore';
import AdminProfile from '../components/AdminProfile';
import RecentActivities from '../components/RecentActivities';
import ApiServices from '../services/ApiServices';
import { ShowDangerToast, ShowSuccessToast } from '../services/toast';
import {
  PieChart, Pie, Cell, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, Tooltip
} from 'recharts';

const COLORS = ['#2B3A67', '#F49D37', '#22C55E', '#6B7280'];

const signOut = () => {
  localStorage.clear();
  window.location.href = '/';
};

function SignIn() {
  return <p>Please sign in to continue.</p>;
}

export default function AdminPanel() {
  const [activeTab, setActiveTab] = useState('Stores');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profile, setProfile] = useState({ username: '', email: '', profilePicture: '' });
  const [stores, setStores] = useState([]);
  const [donationData, setDonationData] = useState({
    donationStatus: [],
    donationsPerStore: [],
    totalStores: 0,
    totalDonations: 0,
    topStore: null,
  });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [editFormData, setEditFormData] = useState({ name: '', ownerEmail: '', username: '', password: '', ownerProfilePicture: null });
  const [currentStoresPage, setCurrentStoresPage] = useState(1);
  const storesPerPage = 5;
  const navigate = useNavigate();
  const role = localStorage.getItem('role');

  useEffect(() => {
    const fetchStores = async () => {
      try {
        const response = await ApiServices.getData('/stores');
        setStores(response.data);
        const userResponse = await ApiServices.getData('/users/me');
        setProfile({
          username: userResponse.data.username,
          email: userResponse.data.email,
          profilePicture: userResponse.data.profilePicture,
        });
      } catch {
        ShowDangerToast('Failed to load stores');
      }
    };
    fetchStores();
  }, []);

  useEffect(() => {
    const fetchDonationData = async () => {
      try {
        const response = await ApiServices.getData('/donations/all-donation');
        const donations = response.data;
        const statusCounts = donations.reduce(
          (acc, donation) => {
            acc.pending += donation.status === 'pending' ? 1 : 0;
            acc.assigned += donation.status === 'assigned' ? 1 : 0;
            acc.completed += donation.status === 'completed' ? 1 : 0;
            acc.rejected += donation.status === 'rejected' ? 1 : 0;
            return acc;
          },
          { pending: 0, assigned: 0, completed: 0, rejected: 0 }
        );
        const donationStatus = [
          { name: 'Pending', value: statusCounts.pending },
          { name: 'Assigned', value: statusCounts.assigned },
          { name: 'Completed', value: statusCounts.completed },
          { name: 'Rejected', value: statusCounts.rejected },
        ].filter(status => status.value > 0);
        const donationsByStore = donations.reduce((acc, donation) => {
          const storeId = donation.storeId;
          acc[storeId] = (acc[storeId] || 0) + 1;
          return acc;
        }, {});
        const donationsPerStore = stores.map(store => ({
          name: store.name,
          donations: donationsByStore[store.id] || 0,
        }));
        const topStoreEntry = Object.entries(donationsByStore).sort((a, b) => b[1] - a[1])[0];
        const topStore = topStoreEntry ? stores.find(store => store.id === parseInt(topStoreEntry[0])) : null;
        setDonationData({
          donationStatus,
          donationsPerStore,
          totalStores: stores.length,
          totalDonations: donations.length,
          topStore: topStore ? { name: topStore.name, donations: topStoreEntry[1] } : null,
        });
      } catch {
        ShowDangerToast('Failed to load donation data');
      }
    };
    if (role === 'superadmin') {
      fetchDonationData();
    }
  }, [role, stores]);

  const tabs = [
    { name: 'Stores', icon: LayoutDashboard, visible: true },
    { name: 'Add Store', icon: Plus, visible: role === 'superadmin' },
    { name: 'Profile', icon: User, visible: true },
    { name: 'Sign Out', icon: LogOut, visible: true },
  ];

  const handleEditStore = (store) => {
    setSelectedStore(store);
    setEditFormData({
      name: store.name,
      ownerEmail: store.owner.email,
      username: store.owner.username,
      password: '',
      ownerProfilePicture: null,
    });
    setIsEditModalOpen(true);
  };

  const handleModalClose = () => {
    setIsEditModalOpen(false);
    setSelectedStore(null);
    setEditFormData({ name: '', ownerEmail: '', username: '', password: '', ownerProfilePicture: null });
  };

  const handleEditFormChange = (e) => {
    const { name, value, files } = e.target;
    if (files) setEditFormData({ ...editFormData, [name]: files[0] });
    else setEditFormData({ ...editFormData, [name]: value });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('name', editFormData.name);
      formData.append('ownerEmail', editFormData.ownerEmail);
      formData.append('username', editFormData.username);
      if (editFormData.password) formData.append('ownerPassword', editFormData.password);
      if (editFormData.ownerProfilePicture) formData.append('ownerProfilePicture', editFormData.ownerProfilePicture);
      await ApiServices.putData(`/stores/${selectedStore.id}`, formData);
      ShowSuccessToast('Store updated successfully');
      const updatedStores = stores.map((store) =>
        store.id === selectedStore.id
          ? {
            ...store,
            name: editFormData.name,
            owner: {
              ...store.owner,
              email: editFormData.ownerEmail,
              username: editFormData.username,
              profilePicture: editFormData.ownerProfilePicture
                ? URL.createObjectURL(editFormData.ownerProfilePicture)
                : store.owner.profilePicture,
            },
          }
          : store
      );
      setStores(updatedStores);
      handleModalClose();
    } catch {
      ShowDangerToast('Failed to update store');
    }
  };

  const handleDeleteStore = async (storeId) => {
    if (window.confirm('Are you sure you want to delete this store?')) {
      try {
        await ApiServices.deleteData(`/stores/${storeId}`);
        setStores(stores.filter(store => store.id !== storeId));
        ShowSuccessToast('Store deleted successfully');
      } catch {
        ShowDangerToast('Failed to delete store');
      }
    }
  };

  const totalStoresPages = Math.ceil(stores.length / storesPerPage);
  const currentStores = stores.slice((currentStoresPage - 1) * storesPerPage, currentStoresPage * storesPerPage);

  const statCards = [
    { label: 'Total Stores', value: donationData.totalStores, icon: Store, iconBg: 'bg-navy-50', iconColor: 'text-navy-600' },
    { label: 'Total Donations', value: donationData.totalDonations, icon: Package, iconBg: 'bg-orange-50', iconColor: 'text-brand-orange' },
    {
      label: 'Top Store',
      value: donationData.topStore ? donationData.topStore.name : 'N/A',
      subtitle: donationData.topStore ? `${donationData.topStore.donations} donations` : '',
      icon: Award,
      iconBg: 'bg-green-50',
      iconColor: 'text-green-600',
      isText: true,
    },
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white px-4 py-2.5 rounded-lg shadow-lg border border-gray-100">
          <p className="text-sm font-semibold text-navy-800">{label}</p>
          <p className="text-sm text-gray-600">{payload[0].value} donations</p>
        </div>
      );
    }
    return null;
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'Stores':
        return (
          <>
            {role === 'superadmin' && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  {statCards.map(stat => {
                    const Icon = stat.icon;
                    return (
                      <div key={stat.label} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{stat.label}</span>
                          <div className={`w-9 h-9 rounded-lg ${stat.iconBg} flex items-center justify-center`}>
                            <Icon className={`w-[18px] h-[18px] ${stat.iconColor}`} />
                          </div>
                        </div>
                        {stat.isText ? (
                          <div>
                            <p className="text-lg font-bold text-navy-700 truncate">{stat.value}</p>
                            {stat.subtitle && <p className="text-xs text-gray-500">{stat.subtitle}</p>}
                          </div>
                        ) : (
                          <div className="text-3xl font-bold text-navy-700">{stat.value}</div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h3 className="text-base font-semibold text-navy-800 mb-4">Donation Status</h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie
                          data={donationData.donationStatus}
                          dataKey="value"
                          outerRadius={70}
                          innerRadius={35}
                          paddingAngle={3}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {donationData.donationStatus.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Legend
                          verticalAlign="bottom"
                          formatter={(value) => <span className="text-xs text-gray-600">{value}</span>}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h3 className="text-base font-semibold text-navy-800 mb-4">Donations Per Store</h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={donationData.donationsPerStore}>
                        <XAxis dataKey="name" stroke="#9CA3AF" fontSize={12} tickLine={false} axisLine={false} />
                        <YAxis stroke="#9CA3AF" fontSize={12} tickLine={false} axisLine={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="donations" fill="#2B3A67" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
                  <h3 className="text-base font-semibold text-navy-800 mb-4">Recent Activities</h3>
                  <RecentActivities />
                </div>
              </>
            )}

            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-lg font-bold text-navy-800">All Stores</h3>
                <p className="text-sm text-gray-500">Click a store to view details.</p>
              </div>

              <div className="divide-y divide-gray-100">
                {currentStores.map((store) => (
                  <div
                    key={store.id}
                    className="flex flex-wrap md:flex-nowrap justify-between items-start md:items-center hover:bg-gray-50 transition-colors p-4 sm:px-6 group cursor-pointer"
                    onClick={() => {
                      if (role === 'superadmin' || role === 'store_manager') {
                        localStorage.setItem('storeId', store.id);
                        localStorage.setItem('storeName', store.name);
                      }
                      navigate(`/store/${store.id}`);
                    }}
                  >
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center gap-2">
                        <p className="text-base font-semibold text-navy-800 group-hover:text-navy-600 transition-colors truncate">{store.name}</p>
                        <ExternalLink className="w-3.5 h-3.5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                      </div>
                      <p className="text-sm text-gray-500">
                        Owner: <span className="text-gray-700 font-medium">{store.owner.username}</span> ({store.owner.email})
                      </p>
                      <div className="flex flex-col sm:flex-row sm:gap-4 text-xs text-brand-orange">
                        <a
                          href={`https://donordispatch.com/store/${store.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Store Link
                        </a>
                        <a
                          href={`https://donordispatch.com/donation/${store?.storeId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Donation Link
                        </a>
                      </div>
                    </div>

                    {role === 'superadmin' && (
                      <div className="flex gap-1 pl-0 md:pl-4 mt-2 md:mt-0">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleEditStore(store); }}
                          className="p-2 text-gray-400 hover:text-navy-600 hover:bg-navy-50 rounded-lg transition-colors"
                          title="Edit Store"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteStore(store.id); }}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete Store"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {totalStoresPages > 1 && (
                <div className="flex justify-between items-center px-6 py-3 border-t border-gray-100">
                  <p className="text-sm text-gray-500">
                    Page {currentStoresPage} of {totalStoresPages}
                  </p>
                  <div className="flex gap-1">
                    <button
                      disabled={currentStoresPage === 1}
                      onClick={() => setCurrentStoresPage((prev) => prev - 1)}
                      className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      disabled={currentStoresPage === totalStoresPages}
                      onClick={() => setCurrentStoresPage((prev) => prev + 1)}
                      className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        );
      case 'Add Store':
        return <AddStore />;
      case 'Profile':
        return <AdminProfile />;
      case 'Sign Out':
        return <SignIn />;
      default:
        return <p></p>;
    }
  };

  const SidebarContent = ({ onClose }) => (
    <div className="flex flex-col h-full">
      <div className="p-5 border-b border-white/10">
        <h1 className="text-lg font-bold text-center text-white">Admin Panel</h1>
      </div>
      <nav className="flex-1 p-3 space-y-1">
        {tabs.filter(tab => tab.visible).map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.name}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.name
                  ? 'bg-white/15 text-white'
                  : 'text-white/70 hover:bg-white/10 hover:text-white'
              }`}
              onClick={() => {
                if (tab.name === 'Sign Out') {
                  signOut();
                } else {
                  setActiveTab(tab.name);
                  if (onClose) onClose();
                }
              }}
            >
              <Icon className="w-4 h-4" />
              {tab.name}
            </button>
          );
        })}
      </nav>
    </div>
  );

  return (
    <div className="flex h-screen font-sans">
      <aside className="bg-navy-700 w-60 hidden md:flex flex-col">
        <SidebarContent />
      </aside>

      {sidebarOpen && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40 md:hidden" onClick={() => setSidebarOpen(false)} />
          <div className="fixed top-0 left-0 w-60 h-full bg-navy-700 z-50 md:hidden">
            <SidebarContent onClose={() => setSidebarOpen(false)} />
          </div>
        </>
      )}

      <main className="flex-1 bg-gray-50 overflow-auto">
        <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-gray-200 px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => setSidebarOpen(true)} className="text-navy-600 md:hidden">
                <Menu className="w-5 h-5" />
              </button>
              <h2 className="text-base font-semibold text-navy-800">{activeTab}</h2>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500 hidden sm:block">{profile.username}</span>
              {profile.profilePicture && (
                <img
                  src={profile.profilePicture}
                  alt="Profile"
                  className="w-8 h-8 rounded-full border-2 border-gray-200 object-cover"
                />
              )}
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6 lg:p-8">{renderContent()}</div>

        {isEditModalOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-bold text-navy-800">Edit Store</h3>
                <button onClick={handleModalClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              <form onSubmit={handleEditSubmit} className="space-y-4">
                {[
                  { label: 'Store Name', name: 'name', type: 'text', required: true },
                  { label: 'Owner Email', name: 'ownerEmail', type: 'email', required: true },
                  { label: 'Owner Username', name: 'username', type: 'text', required: true },
                  { label: 'Password (leave blank to keep)', name: 'password', type: 'password', required: false },
                ].map((field) => (
                  <div key={field.name}>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">{field.label}</label>
                    <input
                      type={field.type}
                      name={field.name}
                      value={editFormData[field.name]}
                      onChange={handleEditFormChange}
                      className="input-field"
                      required={field.required}
                    />
                  </div>
                ))}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Owner Profile Picture</label>
                  <input
                    type="file"
                    name="ownerProfilePicture"
                    accept="image/*"
                    onChange={handleEditFormChange}
                    className="input-field"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={handleModalClose} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                    Cancel
                  </button>
                  <button type="submit" className="px-4 py-2 bg-navy-600 text-white rounded-lg hover:bg-navy-700 text-sm font-medium transition-colors">
                    Update Store
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
